import {
  useCallback,
  useLayoutEffect,
  useEffect,
  useMemo,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { useAppStore } from '../app/state/useAppStore';
import type { ResolvedSourceCrs } from '../crs/shapefileCrs';
import type { EpsgSearchResult } from '../crs/epsgCatalogue';
import {
  importDataFile,
  type DataImportResult,
  type PendingTabularImport,
} from '../data/import/dataImport';
import type {
  PendingShapefileImport,
  ShapefileCrsResolutionResult,
} from '../data/import/shapefileImport';
import type { ImportProgress, ImportSubsetResult } from '../data/import/importPreflight';
import type { ShapefileSelectiveSession } from '../data/import/shapefilePreflight';
import type { GeoPackageSession } from '../data/import/geopackageImport';
import { DropmapProjectError } from '../project/dropmap/errors';
import { PROJECT_FILE_ACCEPT } from '../project/dropmap/filename';
import { isDropmapFileName } from '../project/dropmap/filename';
import { OperationCancelledError } from '../operations/longOperation';
import { LatestOperationCoordinator, type OperationIdentity } from '../operations/latestOperation';
import { DialogShell } from './DialogShell';
import { Icon } from './Icon';
import { ProjectReplacementDialog } from './ProjectReplacementDialog';
import { SpreadsheetImportDialog } from './SpreadsheetImportDialog';
import { GeoPackageContentsDialog } from './GeoPackageContentsDialog';
import { AnchoredMenu } from '../app/shell/AnchoredMenu';
import { ImportScopeDialog } from './ImportScopeDialog';
import { ImportMethodHelp } from './ImportMethodHelp';
import {
  IMPORT_METHODS,
  SUPPORTED_IMPORT_FORMATS,
  subsetPreparedItems,
  type PreparedImportItem,
  type ImportMethod,
} from '../data/import/importWorkflow';

interface DataImporterProps {
  readonly variant?: 'panel' | 'start';
  readonly portalActions?: boolean;
}

interface OperationValue<T> {
  readonly operationId: number;
  readonly value: T;
}
const EMPTY_PREPARED_ITEMS: readonly PreparedImportItem[] = [];

export function DataImporter({ variant = 'panel', portalActions = false }: DataImporterProps) {
  const [actionTarget, setActionTarget] = useState<HTMLElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const methodRef = useRef<ImportMethod | null>(null);
  const [prepared, setPrepared] = useState<OperationValue<readonly PreparedImportItem[]> | null>(
    null,
  );
  const [preparedMethod, setPreparedMethod] = useState<ImportMethod | null>(null);
  const mode = useAppStore((state) => state.mode);
  const collapsed = useAppStore((state) => state.shell.layersPanelCollapsed);
  const openBlankMap = useAppStore((state) => state.openBlankMap);
  useLayoutEffect(() => {
    // The portal host exists only after the sibling workspace commits; resolve it before paint.
    if (!portalActions) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActionTarget(document.querySelector<HTMLElement>(`[data-import-actions="${variant}"]`));
  }, [portalActions, variant, mode, collapsed]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const projectInputRef = useRef<HTMLInputElement | null>(null);
  const addDataButtonRef = useRef<HTMLButtonElement | null>(null);
  const projectButtonRef = useRef<HTMLButtonElement | null>(null);
  const [operationFocusTarget, setOperationFocusTarget] = useState<HTMLElement | null>(null);
  const addImportedVector = useAppStore((state) => state.addImportedVector);
  const addImportedTable = useAppStore((state) => state.addImportedTable);
  const addImportedGeoPackage = useAppStore((state) => state.addImportedGeoPackage);
  const replaceProject = useAppStore((state) => state.replaceProject);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const committingOperationRef = useRef<number | null>(null);
  const [importStatus, setImportStatus] = useState('Reading and validating data locally…');
  const [canCancelImport, setCanCancelImport] = useState(false);
  const [cancelImportMessage, setCancelImportMessage] = useState('Spreadsheet import cancelled.');
  const [pendingShapefile, setPendingShapefile] =
    useState<OperationValue<PendingShapefileImport> | null>(null);
  const [crsQuery, setCrsQuery] = useState('');
  const [crsResults, setCrsResults] = useState<readonly EpsgSearchResult[]>([]);
  const [selectedEpsgCode, setSelectedEpsgCode] = useState<number | null>(null);
  const [isSearchingCrs, setIsSearchingCrs] = useState(false);
  const [pendingProjectFile, setPendingProjectFile] = useState<OperationValue<File> | null>(null);
  const [selectiveSession, setSelectiveSession] =
    useState<OperationValue<ShapefileSelectiveSession> | null>(null);
  const [selectiveCrs, setSelectiveCrs] = useState<ResolvedSourceCrs | null>(null);
  const [selectiveExtent, setSelectiveExtent] = useState<
    import('../data/model').SpatialExtent | null
  >(null);
  const spatialScan = useMemo(() => {
    if (!selectiveSession || !selectiveCrs) return undefined;
    const session = selectiveSession.value;
    return async (rectangle: import('geojson').Polygon, signal: AbortSignal) => {
      const { findShapefileSpatialMatches } = await import('../data/import/shapefilePreflight');
      const matches = await findShapefileSpatialMatches(session, rectangle, selectiveCrs, signal);
      if (
        session.planSubset(
          matches,
          session.preflight.fields.map((field) => field.name),
        ).safety !== 'safe'
      )
        throw new Error('This area selects too much data to import safely. Draw a smaller area.');
      return matches;
    };
  }, [selectiveSession, selectiveCrs]);
  const selectiveEpsgRef = useRef<number | null>(null);
  const [pendingTabular, setPendingTabular] = useState<OperationValue<PendingTabularImport> | null>(
    null,
  );
  const [pendingGeoPackage, setPendingGeoPackage] =
    useState<OperationValue<GeoPackageSession> | null>(null);
  const [coordinator] = useState(() => new LatestOperationCoordinator());
  const crsTitleId = useId();
  const crsSearchRef = useRef<HTMLInputElement | null>(null);
  const prepareItems = useCallback(
    (operationId: number, items: readonly PreparedImportItem[]) => {
      if (!coordinator.isCurrent(operationId)) return;
      setPrepared({ operationId, value: items });
      setPreparedMethod(methodRef.current);
    },
    [coordinator],
  );

  useEffect(() => () => coordinator.dispose(), [coordinator]);
  useEffect(() => () => pendingTabular?.value.workbook?.dispose(), [pendingTabular]);
  useEffect(() => () => pendingGeoPackage?.value.dispose(), [pendingGeoPackage]);

  const resetCrsForm = useCallback(() => {
    setPendingShapefile(null);
    setCrsQuery('');
    setCrsResults([]);
    setSelectedEpsgCode(null);
    setIsSearchingCrs(false);
  }, []);

  useEffect(() => {
    if (!pendingShapefile || crsQuery.trim().length < 2) return;

    const operationId = pendingShapefile.operationId;

    let cancelled = false;
    const timeout = window.setTimeout(() => {
      void import('../crs/epsgCatalogue')
        .then(({ searchEpsgCatalogue }) => {
          if (!cancelled && coordinator.isCurrent(operationId)) {
            setCrsResults(searchEpsgCatalogue(crsQuery));
          }
        })
        .catch(() => {
          if (!cancelled && coordinator.isCurrent(operationId)) {
            setCrsResults([]);
            setErrorMessage('Placeio Maps could not load the offline CRS catalogue. Try again.');
          }
        })
        .finally(() => {
          if (!cancelled && coordinator.isCurrent(operationId)) setIsSearchingCrs(false);
        });
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [coordinator, crsQuery, pendingShapefile]);

  const beginOperation = useCallback(
    (returnFocusTo: HTMLElement | null): OperationIdentity => {
      const operation = coordinator.start();
      setOperationFocusTarget(returnFocusTo);
      pendingTabular?.value.workbook?.dispose();
      pendingGeoPackage?.value.dispose();
      setErrorMessage(null);
      setSuccessMessage(null);
      setPendingTabular(null);
      setPrepared(null);
      setPendingGeoPackage(null);
      setSelectiveSession(null);
      setSelectiveCrs(null);
      setSelectiveExtent(null);
      selectiveEpsgRef.current = null;
      setPendingProjectFile(null);
      resetCrsForm();
      setCanCancelImport(false);
      return operation;
    },
    [coordinator, pendingTabular, pendingGeoPackage, resetCrsForm],
  );

  const finishBusy = useCallback(
    (operationId: number) => {
      // A stale finally block must not clear busy state owned by a newer operation.
      if (!coordinator.isCurrent(operationId)) return;
      setIsImporting(false);
      setCanCancelImport(false);
    },
    [coordinator],
  );

  const importFile = useCallback(
    async (file: File) => {
      const operation = beginOperation(addDataButtonRef.current);
      setIsImporting(true);
      const isXlsx = /\.xlsx$/i.test(file.name);
      const isGeoPackage = /\.gpkg$/i.test(file.name);
      setCanCancelImport(isXlsx || isGeoPackage);
      setCancelImportMessage(
        isGeoPackage ? 'GeoPackage import cancelled.' : 'Spreadsheet import cancelled.',
      );
      setImportStatus(
        isXlsx
          ? 'Inspecting workbook locally…'
          : isGeoPackage
            ? 'Inspecting GeoPackage locally…'
            : 'Reading and validating data locally…',
      );

      let result: DataImportResult;
      try {
        result = await importDataFile(file, {
          signal: operation.signal,
          onXlsxStage: (stage) => {
            if (!coordinator.isCurrent(operation.id)) return;
            setImportStatus(
              stage === 'loading-library'
                ? 'Starting spreadsheet worker…'
                : stage === 'reading-workbook'
                  ? 'Reading workbook locally…'
                  : 'Normalizing worksheet locally…',
            );
          },
        });
      } catch (cause) {
        if (!coordinator.isCurrent(operation.id)) return;
        if (cause instanceof OperationCancelledError) {
          setSuccessMessage(
            isGeoPackage ? 'GeoPackage import cancelled.' : 'Spreadsheet import cancelled.',
          );
        } else {
          setErrorMessage('Placeio Maps could not load the local import module. Try again.');
        }
        finishBusy(operation.id);
        coordinator.finish(operation.id);
        return;
      }
      if (!coordinator.isCurrent(operation.id)) {
        if (result.status === 'needs-tabular-intent') result.value.workbook?.dispose();
        if (result.status === 'needs-geopackage') result.value.dispose();
        return;
      }
      finishBusy(operation.id);

      if (result.status === 'error') {
        setErrorMessage(result.error.message);
        coordinator.finish(operation.id);
        return;
      }
      if (result.status === 'needs-crs') {
        setPendingShapefile({ operationId: operation.id, value: result.value });
        return;
      }
      if (result.status === 'needs-geopackage') {
        setPendingGeoPackage({ operationId: operation.id, value: result.value });
        return;
      }
      if (result.status === 'needs-subset') {
        setIsImporting(true);
        try {
          const { preflightShapefileZip } = await import('../data/import/shapefilePreflight');
          const preflight = await preflightShapefileZip(result.value.file, {
            signal: operation.signal,
          });
          if (!coordinator.isCurrent(operation.id)) return;
          if (preflight.ok) {
            setSelectiveSession({ operationId: operation.id, value: preflight.value });
            methodRef.current = null;
          } else {
            setErrorMessage(preflight.error.message);
            finishBusy(operation.id);
            coordinator.finish(operation.id);
          }
        } catch {
          if (!coordinator.isCurrent(operation.id)) return;
          setErrorMessage('Placeio Maps could not inspect this dataset for subset import.');
          finishBusy(operation.id);
          coordinator.finish(operation.id);
        } finally {
          finishBusy(operation.id);
        }
        return;
      }

      if (result.status === 'needs-tabular-intent') {
        if (result.value.workbook) {
          performance.measure('dropmap-xlsx-worker-parse', {
            start: 0,
            duration: result.value.workbook.workerParseDurationMs,
          });
        }
        setPendingTabular({ operationId: operation.id, value: result.value });
      } else if (result.status === 'imported-many') {
        prepareItems(operation.id, result.values);
      } else {
        prepareItems(operation.id, [result.value]);
      }
    },
    [beginOperation, coordinator, finishBusy, prepareItems],
  );

  const openSelectiveImport = useCallback(
    async (file: File) => {
      const operation = beginOperation(addDataButtonRef.current);
      setIsImporting(true);
      setImportStatus('Inspecting dataset locally…');
      try {
        const { preflightShapefileZip, shapefilePreviewExtent } =
          await import('../data/import/shapefilePreflight');
        const result = await preflightShapefileZip(file, { signal: operation.signal });
        if (!coordinator.isCurrent(operation.id)) return;
        if (result.ok) {
          setPreparedMethod(methodRef.current);
          setSelectiveSession({ operationId: operation.id, value: result.value });
          const { resolvePrjCrs } = await import('../data/import/shapefileImport');
          const declared = result.value.prjText ? await resolvePrjCrs(result.value.prjText) : null;
          if (!coordinator.isCurrent(operation.id)) return;
          if (declared?.ok) {
            setSelectiveCrs(declared.value);
            setSelectiveExtent(shapefilePreviewExtent(result.value, declared.value));
          } else {
            const sample =
              result.value.shx.recordCount > 0
                ? await result.value.importSubset(
                    {
                      matchingRecordIndexes: Uint32Array.of(0),
                      matchCount: 1,
                      totalFeatureCount: result.value.shx.recordCount,
                      selectedGeometryBytes: 8 + result.value.shx.contentBytes[0]!,
                      selectedAttributeBytes: 0,
                      selectedFields: [],
                      safety: 'safe',
                    },
                    { signal: operation.signal },
                  )
                : { type: 'FeatureCollection' as const, features: [] };
            if (!coordinator.isCurrent(operation.id)) return;
            setPendingShapefile({
              operationId: operation.id,
              value: {
                kind: 'shapefile-crs-resolution',
                reason: result.value.prjText ? 'unsupported-prj' : 'missing-prj',
                message: 'Choose the source coordinate system before filtering this Shapefile.',
                archiveFileName: file.name,
                datasetName: result.value.selected.datasetName,
                suggestedLayerName: result.value.selected.suggestedLayerName,
                componentFileNames: result.value.selected.componentFileNames,
                rawData: sample,
                ...(result.value.prjText ? { declaredWkt: result.value.prjText } : {}),
              },
            });
          }
        } else if (result.preflight?.fullImportSafety === 'SAFE_FULL') {
          await importFile(file);
          return;
        } else {
          setErrorMessage(result.error.message);
          finishBusy(operation.id);
          coordinator.finish(operation.id);
        }
      } catch {
        if (!coordinator.isCurrent(operation.id)) return;
        setErrorMessage('Placeio Maps could not inspect this dataset for subset import.');
        finishBusy(operation.id);
        coordinator.finish(operation.id);
      } finally {
        finishBusy(operation.id);
      }
    },
    [beginOperation, coordinator, finishBusy, importFile],
  );

  const openProjectFile = useCallback(
    async (file: File, existingOperation?: OperationIdentity) => {
      const operation = existingOperation ?? beginOperation(projectButtonRef.current);
      if (!coordinator.isCurrent(operation.id)) return;
      setPendingProjectFile(null);
      setIsImporting(true);
      setImportStatus('Opening project locally…');
      try {
        const { readDropmapProjectFile } = await import('../project/dropmap/browserFile');
        const project = await readDropmapProjectFile(file, operation.signal);
        if (!coordinator.isCurrent(operation.id)) return;
        replaceProject(project);
        finishBusy(operation.id);
        coordinator.finish(operation.id);
      } catch (error) {
        if (!coordinator.isCurrent(operation.id)) return;
        setErrorMessage(
          error instanceof DropmapProjectError
            ? error.userMessage
            : 'Placeio Maps could not open this project. Try again.',
        );
        finishBusy(operation.id);
        coordinator.finish(operation.id);
      } finally {
        finishBusy(operation.id);
      }
    },
    [beginOperation, coordinator, finishBusy, replaceProject],
  );

  const routeFile = useCallback(
    (file: File) => {
      if (isDropmapFileName(file.name)) {
        if (variant === 'panel') {
          const returnFocusTo =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          const operation = beginOperation(returnFocusTo);
          setPendingProjectFile({ operationId: operation.id, value: file });
        } else {
          void openProjectFile(file);
        }
        return;
      }
      if (/\.zip$/i.test(file.name)) void openSelectiveImport(file);
      else void importFile(file);
    },
    [beginOperation, importFile, openSelectiveImport, openProjectFile, variant],
  );

  const importDroppedFiles = useCallback(
    (files: FileList, dropped = true) => {
      if (dropped) methodRef.current = null;
      if (files.length !== 1) {
        const chosen = Array.from({ length: files.length }, (_, index) =>
          files.item(index)!,
        ).filter(Boolean);
        if (!chosen.length) return;
        const method = methodRef.current;
        const operation = beginOperation(addDataButtonRef.current);
        setIsImporting(true);
        setCanCancelImport(true);
        setImportStatus('Preparing Shapefile companions locally…');
        void import('../data/import/acquireFiles')
          .then(({ acquireDataFiles }) => acquireDataFiles(chosen, operation.signal))
          .then((file) => {
            if (coordinator.isCurrent(operation.id)) {
              methodRef.current = method;
              routeFile(file);
            }
          })
          .catch((error) => {
            if (coordinator.isCurrent(operation.id)) {
              setErrorMessage(
                error instanceof Error ? error.message : 'Could not prepare these files.',
              );
              // Clear busy while this operation still owns it. Finishing first makes the
              // guarded finally cleanup stale and leaves acquisition controls disabled.
              finishBusy(operation.id);
              coordinator.finish(operation.id);
            }
          })
          .finally(() => finishBusy(operation.id));
        return;
      }

      const file = files.item(0);
      if (file) routeFile(file);
    },
    [routeFile, beginOperation, coordinator, finishBusy],
  );

  useEffect(() => {
    if (variant !== 'panel') return;
    const mapTarget = document.querySelector<HTMLElement>('[data-map-drop-target="true"]');
    if (!mapTarget) return;

    const activate = (event: globalThis.DragEvent) => {
      event.preventDefault();
      mapTarget.classList.add('map-drop-active');
    };
    const deactivate = () => mapTarget.classList.remove('map-drop-active');
    const drop = (event: globalThis.DragEvent) => {
      event.preventDefault();
      deactivate();
      if (event.dataTransfer) importDroppedFiles(event.dataTransfer.files);
    };

    mapTarget.addEventListener('dragenter', activate);
    mapTarget.addEventListener('dragover', activate);
    mapTarget.addEventListener('dragleave', deactivate);
    mapTarget.addEventListener('drop', drop);
    return () => {
      mapTarget.removeEventListener('dragenter', activate);
      mapTarget.removeEventListener('dragover', activate);
      mapTarget.removeEventListener('dragleave', deactivate);
      mapTarget.removeEventListener('drop', drop);
    };
  }, [importDroppedFiles, variant]);

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files;
    if (files?.length) importDroppedFiles(files, false);
    event.currentTarget.value = '';
  };

  const handleProjectInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.item(0);
    event.currentTarget.value = '';
    if (file) void openProjectFile(file);
  };

  const importSubset = async (
    subset: ImportSubsetResult,
    options: { readonly signal: AbortSignal; readonly onProgress: (value: ImportProgress) => void },
  ) => {
    if (!selectiveSession) return;
    const { operationId, value: session } = selectiveSession;
    if (!coordinator.isCurrent(operationId)) return;
    const operation = coordinator.current(operationId);
    if (!operation) return;
    options = { ...options, signal: AbortSignal.any([operation.signal, options.signal]) };
    const rawData = await session.importSubset(subset, options);
    options.signal.throwIfAborted();
    if (!coordinator.isCurrent(operationId)) return;
    options.onProgress({ phase: 'reprojecting' });
    const { finishParsedShapefile } = await import('../data/import/shapefileImport');
    if (!coordinator.isCurrent(operationId)) return;
    let result = await finishParsedShapefile(
      session.archive.name,
      {
        datasetName: session.selected.datasetName,
        suggestedLayerName: session.selected.suggestedLayerName,
        componentFileNames: session.selected.componentFileNames,
        ...(session.prjText ? { prjText: session.prjText } : {}),
      },
      rawData,
    );
    if (selectiveEpsgRef.current !== null) {
      const { resolvePendingShapefileCrs } = await import('../data/import/shapefileImport');
      const resolved = await resolvePendingShapefileCrs(
        {
          kind: 'shapefile-crs-resolution',
          reason: 'missing-prj',
          message: '',
          archiveFileName: session.archive.name,
          datasetName: session.selected.datasetName,
          suggestedLayerName: session.selected.suggestedLayerName,
          componentFileNames: session.selected.componentFileNames,
          rawData,
        },
        { epsgCode: selectiveEpsgRef.current },
      );
      result = resolved.ok
        ? { status: 'imported', value: resolved.value }
        : { status: 'error', error: resolved.error };
    }
    options.signal.throwIfAborted();
    if (!coordinator.isCurrent(operationId)) return;
    if (result.status === 'error') throw new Error(result.error.message);
    if (result.status === 'needs-subset') throw new Error('Unexpected nested subset request.');
    finishBusy(operationId);
    setSelectiveSession(null);
    if (result.status === 'needs-crs') {
      setPendingShapefile({ operationId, value: result.value });
    } else {
      addImportedVector(result.value);
      coordinator.finish(operationId);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    importDroppedFiles(event.dataTransfer.files);
  };

  const handleCrsResolution = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pendingShapefile) return;

    if (selectedEpsgCode === null) {
      setErrorMessage('Choose the source coordinate reference system.');
      return;
    }

    const { operationId, value: shapefile } = pendingShapefile;
    if (!coordinator.isCurrent(operationId)) return;
    setErrorMessage(null);
    setIsImporting(true);
    let result: ShapefileCrsResolutionResult;
    try {
      const { resolvePendingShapefileCrs } = await import('../data/import/shapefileImport');
      result = await resolvePendingShapefileCrs(shapefile, {
        epsgCode: selectedEpsgCode,
      });
    } catch {
      if (!coordinator.isCurrent(operationId)) return;
      setErrorMessage(
        'Placeio Maps could not load the local CRS transformation module. Try again.',
      );
      finishBusy(operationId);
      return;
    }
    if (!coordinator.isCurrent(operationId)) return;
    finishBusy(operationId);

    if (!result.ok) {
      setErrorMessage(result.error.message);
      return;
    }

    resetCrsForm();
    if (selectiveSession) {
      const { resolveEpsgCatalogueCrs } = await import('../crs/epsgCatalogue');
      const resolved = await resolveEpsgCatalogueCrs(selectedEpsgCode);
      if (!coordinator.isCurrent(operationId)) return;
      if (!resolved.ok) {
        setErrorMessage(resolved.error.message);
        return;
      }
      selectiveEpsgRef.current = selectedEpsgCode;
      setSelectiveCrs(resolved.value);
      const { shapefilePreviewExtent } = await import('../data/import/shapefilePreflight');
      if (coordinator.isCurrent(operationId))
        setSelectiveExtent(shapefilePreviewExtent(selectiveSession.value, resolved.value));
      return;
    }
    prepareItems(operationId, [result.value]);
  };

  const chooseMethod = (method: ImportMethod | null) => {
    methodRef.current = method;
    setMenuOpen(false);
    inputRef.current?.click();
  };
  const loadingStatus = isImporting ? (
    <div className="import-status" role="status">
      <div className="import-loading-copy">
        <span>{importStatus}</span>
        <div className="import-progress" role="progressbar" aria-label="File loading" />
      </div>
      {canCancelImport ? (
        <button
          onClick={() => {
            const operationId = coordinator.currentId;
            if (operationId === null || !coordinator.cancel(operationId)) return;
            pendingGeoPackage?.value.dispose();
            setPendingGeoPackage(null);
            setCanCancelImport(false);
            setIsImporting(false);
            setSuccessMessage(cancelImportMessage);
          }}
          type="button"
        >
          Cancel import
        </button>
      ) : null}
    </div>
  ) : null;
  const actions = (
    <div className={`data-importer data-importer-${variant}`}>
      {variant === 'start' ? (
        <div className="start-import-tiles">
          {IMPORT_METHODS.map((method) => (
            <div className="start-import-card" key={method.id}>
              <button
                className="start-import-tile"
                type="button"
                disabled={isImporting}
                onClick={() => chooseMethod(method.id)}
              >
                <Icon name={method.icon} />
                <strong>{method.label}</strong>
              </button>
              <ImportMethodHelp label={method.label} text={method.help} />
            </div>
          ))}
          <button className="start-import-tile" type="button" onClick={openBlankMap}>
            <Icon name="map" />
            <strong>Open blank map</strong>
          </button>
          <button
            className="start-import-tile"
            type="button"
            disabled={isImporting}
            onClick={() => projectInputRef.current?.click()}
            ref={projectButtonRef}
          >
            <Icon name="upload" />
            <strong>Open project</strong>
          </button>
        </div>
      ) : null}
      <div
        aria-label={
          variant === 'start'
            ? isImporting
              ? 'Loading local file'
              : 'Drop a file here or choose a file'
            : 'Add Data drop zone'
        }
        tabIndex={variant === 'start' && !isImporting ? 0 : undefined}
        onClick={
          variant === 'start'
            ? (event) => {
                if (event.target instanceof HTMLInputElement || isImporting) return;
                chooseMethod(null);
              }
            : undefined
        }
        onKeyDown={
          variant === 'start'
            ? (event) => {
                if (event.target !== event.currentTarget || isImporting) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  chooseMethod(null);
                }
              }
            : undefined
        }
        className={[
          'data-drop-zone',
          `data-drop-zone-${variant}`,
          isDragging ? 'data-drop-zone-active' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-drop-target="spatial-data"
        onDragEnter={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        role={variant === 'start' && !isImporting ? 'button' : 'region'}
      >
        {variant === 'start' ? (
          isImporting ? (
            loadingStatus
          ) : (
            <div className="drop-zone-message">
              <Icon name="upload" />
              <strong>Drop a file here</strong>
              <span>Drop data to choose an import method, or a .placeio project to open it.</span>
            </div>
          )
        ) : null}
        {variant === 'panel' ? (
          <div className="file-picker-actions">
            <button
              className="file-picker-action primary-action"
              disabled={isImporting}
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={variant === 'panel' ? menuOpen : undefined}
              aria-haspopup={variant === 'panel' ? 'menu' : undefined}
              ref={addDataButtonRef}
              type="button"
            >
              {isImporting ? (
                'Loading…'
              ) : (
                <>
                  <Icon name="plus" />
                  Add data
                </>
              )}
            </button>
          </div>
        ) : null}
        <input
          accept=".csv,.xlsx,.geojson,.json,.gpkg,.kml,.kmz,.zip,.shp,.shx,.dbf,.prj,.cpg,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/geo+json,application/json,application/zip"
          multiple
          aria-label="Choose a supported data file"
          className="visually-hidden"
          disabled={isImporting}
          onChange={handleFileInput}
          ref={inputRef}
          tabIndex={-1}
          type="file"
        />
        {variant === 'start' ? (
          <input
            accept={PROJECT_FILE_ACCEPT}
            aria-label="Choose a Placeio project file"
            className="visually-hidden"
            disabled={isImporting}
            onChange={handleProjectInput}
            ref={projectInputRef}
            tabIndex={-1}
            type="file"
          />
        ) : null}
      </div>
      <AnchoredMenu
        anchorRef={addDataButtonRef}
        label="Add data"
        className="add-data-menu"
        open={menuOpen}
        onDismiss={() => setMenuOpen(false)}
      >
        <div className="add-data-introduction">
          <strong>Add a file</strong>
          <p>{SUPPORTED_IMPORT_FORMATS}</p>
        </div>
        {IMPORT_METHODS.map((method) => (
          <button
            role="menuitem"
            aria-label={method.label}
            key={method.id}
            type="button"
            onClick={() => chooseMethod(method.id)}
          >
            <Icon name={method.icon} />
            <span>
              <strong>{method.label}</strong>
              <small>{method.description}</small>
            </span>
          </button>
        ))}
      </AnchoredMenu>
    </div>
  );
  return (
    <>
      {portalActions ? (actionTarget ? createPortal(actions, actionTarget) : null) : actions}
      {prepared ? (
        <ImportScopeDialog
          items={prepared.value}
          initialMethod={preparedMethod}
          returnFocusTo={operationFocusTarget}
          onCancel={() => {
            coordinator.cancel(prepared.operationId);
            setPrepared(null);
          }}
          onCommit={(matches, fields) => {
            if (!coordinator.isCurrent(prepared.operationId)) return;
            const items = matches
              ? subsetPreparedItems(prepared.value, matches, fields)
              : prepared.value;
            if (!items.length) return;
            const only = items.length === 1 ? items[0] : undefined;
            if (only) {
              if ('data' in only) addImportedVector(only);
              else addImportedTable(only);
            } else
              addImportedGeoPackage({
                items,
                vectors: items.filter(
                  (item): item is import('../data/import/model').ImportedVectorData =>
                    'data' in item,
                ),
                tables: items.filter(
                  (item): item is import('../data/import/tabularImport').ImportedTableData =>
                    !('data' in item),
                ),
                warnings: [],
              });
            coordinator.finish(prepared.operationId);
            setPrepared(null);
          }}
        />
      ) : null}

      {variant === 'panel' ? loadingStatus : null}

      {pendingShapefile ? (
        <DialogShell
          className="crs-resolution"
          closeOnEscape={!isImporting}
          element="form"
          initialFocusRef={crsSearchRef}
          onClose={() => {
            if (!coordinator.cancel(pendingShapefile.operationId)) return;
            resetCrsForm();
            setSelectiveSession(null);
            setErrorMessage(null);
            setIsImporting(false);
          }}
          onSubmit={handleCrsResolution}
          returnFocusTo={operationFocusTarget}
          titleId={crsTitleId}
        >
          <p className="eyebrow">Source CRS required</p>
          <h2 id={crsTitleId}>Choose the coordinate system</h2>
          <p role="status">{pendingShapefile.value.message}</p>

          <label>
            Search by EPSG code or CRS name
            <input
              aria-label="Search coordinate systems"
              autoComplete="off"
              onChange={(event) => {
                const query = event.currentTarget.value;
                setCrsQuery(query);
                setCrsResults([]);
                setSelectedEpsgCode(null);
                setIsSearchingCrs(query.trim().length >= 2);
                setErrorMessage(null);
              }}
              placeholder="For example: 28356 or GDA94 MGA 56"
              ref={crsSearchRef}
              type="search"
              value={crsQuery}
            />
          </label>

          {isSearchingCrs ? (
            <p className="crs-search-status" role="status">
              Searching the offline CRS catalogue...
            </p>
          ) : null}

          {!isSearchingCrs && crsQuery.trim().length >= 2 && crsResults.length === 0 ? (
            <p className="crs-search-status" role="status">
              No matching EPSG records were found.
            </p>
          ) : null}

          {crsResults.length > 0 ? (
            <ul aria-label="Coordinate system search results" className="crs-search-results">
              {crsResults.map((result) => (
                <li key={result.code}>
                  <label
                    className={result.availability === 'supported' ? '' : 'crs-result-unsupported'}
                  >
                    <input
                      checked={selectedEpsgCode === result.code}
                      disabled={result.availability !== 'supported'}
                      name="source-crs"
                      onChange={() => setSelectedEpsgCode(result.code)}
                      type="radio"
                      value={result.code}
                    />
                    <span>
                      <strong>{result.identifier}</strong> - {result.name}
                      {result.availability !== 'supported' && result.unsupportedReason ? (
                        <small>
                          {result.availability === 'external-resource-required'
                            ? 'External grid required'
                            : 'Unavailable'}{' '}
                          - {result.unsupportedReason}
                        </small>
                      ) : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="crs-resolution-actions">
            <button
              className="primary-action"
              disabled={isImporting || selectedEpsgCode === null}
              type="submit"
            >
              Import with this CRS
            </button>
            <button
              disabled={isImporting}
              onClick={() => {
                coordinator.cancel(pendingShapefile.operationId);
                resetCrsForm();
                setErrorMessage(null);
              }}
              type="button"
            >
              Cancel import
            </button>
          </div>
        </DialogShell>
      ) : null}

      {errorMessage ? (
        <p className="import-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      {successMessage ? (
        <p className="import-status" role="status">
          {successMessage}
        </p>
      ) : null}
      {pendingProjectFile ? (
        <ProjectReplacementDialog
          confirmLabel="Open project"
          description="Your current map will be replaced."
          onCancel={() => {
            coordinator.cancel(pendingProjectFile.operationId);
            setPendingProjectFile(null);
          }}
          onConfirm={() => {
            const operation = coordinator.current(pendingProjectFile.operationId);
            const file = pendingProjectFile.value;
            setPendingProjectFile(null);
            if (operation) void openProjectFile(file, operation);
          }}
          returnFocusTo={operationFocusTarget}
          title="Open another project?"
        />
      ) : null}
      {selectiveSession && !pendingShapefile ? (
        <ImportScopeDialog
          items={EMPTY_PREPARED_ITEMS}
          initialMethod={preparedMethod}
          error={errorMessage}
          committing={isImporting}
          selectiveSession={selectiveSession.value}
          spatialScan={spatialScan}
          spatialExtent={selectiveExtent}
          onCancel={() => {
            finishBusy(selectiveSession.operationId);
            coordinator.cancel(selectiveSession.operationId);
            setSelectiveSession(null);
          }}
          onCommit={(matches, fields) => {
            const { operationId, value: session } = selectiveSession;
            const operation = coordinator.current(operationId);
            if (!operation || committingOperationRef.current === operationId) return;
            committingOperationRef.current = operationId;
            setIsImporting(true);
            void (async () => {
              const selected =
                matches ??
                (await session.findMatches({ conditions: [] }, { signal: operation.signal }));
              const subset = session.planSubset(
                selected,
                fields ?? session.preflight.fields.map((field) => field.name),
              );
              await importSubset(subset, { signal: operation.signal, onProgress: () => undefined });
            })()
              .catch((error) => {
                if (coordinator.isCurrent(operationId))
                  setErrorMessage(error instanceof Error ? error.message : 'Subset import failed.');
              })
              .finally(() => {
                if (committingOperationRef.current === operationId)
                  committingOperationRef.current = null;
                finishBusy(operationId);
              });
          }}
          onImportSubset={importSubset}
          returnFocusTo={operationFocusTarget}
        />
      ) : null}
      {pendingGeoPackage ? (
        <GeoPackageContentsDialog
          key={pendingGeoPackage.operationId}
          session={pendingGeoPackage.value}
          working={isImporting}
          error={errorMessage}
          onClose={() => {
            coordinator.cancel(pendingGeoPackage.operationId);
            pendingGeoPackage.value.dispose();
            setPendingGeoPackage(null);
          }}
          onImport={(names) => {
            const { operationId, value: session } = pendingGeoPackage;
            if (!coordinator.isCurrent(operationId)) return;
            setIsImporting(true);
            setCanCancelImport(true);
            setErrorMessage(null);
            void session
              .importSelected(names, (title) => {
                if (coordinator.isCurrent(operationId)) setImportStatus(`Importing ${title}…`);
              })
              .then((imported) => {
                if (!coordinator.isCurrent(operationId)) return;
                prepareItems(operationId, imported.items);
                setSuccessMessage(
                  imported.warnings.length
                    ? imported.warnings.join(' ')
                    : `Imported ${imported.items.length} GeoPackage contents.`,
                );
                setPendingGeoPackage(null);
              })
              .catch((error) => {
                if (coordinator.isCurrent(operationId))
                  setErrorMessage(
                    error instanceof Error ? error.message : 'GeoPackage import failed.',
                  );
              })
              .finally(() => finishBusy(operationId));
          }}
          returnFocusTo={operationFocusTarget}
        />
      ) : null}
      {pendingTabular ? (
        <SpreadsheetImportDialog
          onCancel={() => {
            coordinator.cancel(pendingTabular.operationId);
            pendingTabular.value.workbook?.dispose();
            setPendingTabular(null);
            setSuccessMessage('Spreadsheet import cancelled.');
          }}
          onImportPoints={(imported) => {
            if (!coordinator.isCurrent(pendingTabular.operationId)) return;
            prepareItems(pendingTabular.operationId, [imported]);
            pendingTabular.value.workbook?.dispose();
            setPendingTabular(null);
            setSuccessMessage(null);
          }}
          onImportTable={(table) => {
            if (!coordinator.isCurrent(pendingTabular.operationId)) return;
            prepareItems(pendingTabular.operationId, [table]);
            pendingTabular.value.workbook?.dispose();
            setPendingTabular(null);
            setSuccessMessage(null);
          }}
          pending={pendingTabular.value}
          returnFocusTo={operationFocusTarget}
        />
      ) : null}
    </>
  );
}
